import { useSize } from 'ahooks'
import classNames from 'classnames'
import { useSelector } from 'react-redux'
import { Skeleton, Flex, Row, Col } from 'antd'

import keys from '@/config/keys'
import links from '@/config/links'
import isEmpty from '@/utils/isEmpty'
import { getListOf } from '@/utils/getListOf'
import { getHeaderHeightVar } from '@/utils/helpers'

const AppSkeletonBase = (props) => {
	const isFreshInitAppLoad = !window[keys.APP_HAS_NAVIGATION_ACTIVITY]

	let {
		showHeader: showHeaderProp,
		withContainer = true,
		justCardsUI = false,
		horizontalCardUI = false,
		rrd_pathname = '', // 'react-router-dom' pathname | Not always available - specially app init time where <Routes> is not rendered
		outerContentWrapperClass = 'w-full px-5',
		mainContentWrapperClass = '',
		uiWrapperClass = '',
		uiWrapperStyle = {},
	} = props

	let contentWrapperMarginTop = 32
	let marginTopCalcLogic = true
	let showHeader = showHeaderProp ?? true

	const headerSize = useSize(() => document.getElementById(keys.APP_HEADER))
	if (headerSize?.height && isEmpty(showHeaderProp)) {
		showHeader = false /* If header is visible & user didn't specify showHeader via prop, hide it */
	}

	const {
		firstTimeCallingPageSchema,
		showingBanner,
		//  bannerHeight
	} = useSelector((state) => state.theme)
	// const _bannerHeight =
	// 	(showingBanner ? Number(String(bannerHeight).replace('px', '').replace('%', '')) : 0) || 0

	let smHeader = false
	let HEAD
	let CONTENT = (
		<Row gutter={[30, 30]}>
			<Col span={24} md={12}>
				<Skeleton.Node active fullSize className='!h-[440px] !w-full !rounded-3xl md:!h-[340px]'>
					<Flex vertical gap={20} className='h-full w-full px-12 py-8'>
						<Skeleton active />
						<Flex gap={20} wrap='wrap'>
							<Skeleton.Button active size={`large`} shape={`round`} className='!h-14 !w-52' />
							<Skeleton.Button active size={`large`} shape={`round`} className='!h-14 !w-52' />
						</Flex>
					</Flex>
				</Skeleton.Node>
			</Col>
			<Col span={24} md={12}>
				<Skeleton.Node active fullSize className='!h-[440px] !w-full !rounded-3xl md:!h-[340px]'>
					<Row gutter={[30, 30]} className='h-full w-full p-8'>
						<Col span={24} md={12}>
							<Skeleton.Image active className='!h-full !w-full !rounded-3xl' />
						</Col>
						<Col span={24} md={12}>
							<Flex gap={20} vertical>
								<Skeleton active />
								<Skeleton.Button active size={`large`} shape={`round`} className='!h-14 !w-52' />
							</Flex>
						</Col>
					</Row>
				</Skeleton.Node>
			</Col>
			<Col span={24}>
				<Skeleton.Node active fullSize className='!h-[540px] !w-full !rounded-3xl md:!h-[440px]'>
					<Flex vertical gap={20} className='h-full w-full p-8'>
						<Skeleton active />
						<Skeleton active />
						<Flex gap={20} wrap='wrap'>
							<Skeleton.Button active size={`large`} shape={`round`} className='!h-14 !w-52' />
							<Skeleton.Button active size={`large`} shape={`round`} className='!h-14 !w-52' />
						</Flex>
					</Flex>
				</Skeleton.Node>
			</Col>
		</Row>
	)

	if (
		['/checkout'].some((path) => {
			const route = location.pathname
			// First segment can be the event name but still in the app(links.js) no checkout path starting with /checkout
			const segments = route.split('/').filter(Boolean).slice(1)
			return segments.includes(path)
		})
	) {
		smHeader = true
		CONTENT = (
			<div className='flex-center'>
				<CheckoutLoadingShimmer />
			</div>
		)
	} else if (
		[links.your_account.path].some((path) => location.pathname.includes(path.replace('/*', '')))
	) {
		showHeader = false

		CONTENT = (
			<>
				<div className='flex-center mb-10 w-full'>
					<Skeleton.Button active size={`large`} className='!h-16 !w-full !max-w-sm !rounded-lg' />
				</div>

				<Skeleton.Button active size={`large`} className='!h-16 !w-full !rounded-lg' />

				<div className='flex-center w-full'>
					<div className='mt-12 w-full max-w-xl'>
						{getListOf(6).map((i) => (
							<Skeleton key={i} className='!w-full' active paragraph={{ rows: 4 }} />
						))}
					</div>
				</div>
			</>
		)
	} else if (
		[links.login.path, links.register.path, links.pr.path, links.rp.path].some((path) =>
			location.pathname.includes(path),
		)
	) {
		smHeader = true
		CONTENT = (
			<Row justify='center' align='middle'>
				<Col span={22}>
					<Row justify='center' align='middle'>
						<Col span={22}>
							<Flex vertical align='center' className='mb-10 mt-[24px] lg:mb-16 lg:mt-[32px]'>
								<Skeleton.Button active size={`large`} className='!h-14 !w-52' />
							</Flex>
						</Col>
						<Col span={22} md={14} lg={10} className='max-w-xl'>
							<Skeleton.Node
								active
								fullSize
								className='!h-[440px] !w-full !rounded-3xl md:!h-[340px]'
							>
								<Row gutter={[30, 30]} className='flex-center h-full w-full p-8'>
									<Col span={24}>
										<Flex gap={20} vertical>
											<Skeleton active />
											<Row justify={`center`}>
												<Skeleton.Button
													active
													size={`large`}
													shape={`round`}
													className='!h-14 !w-full'
												/>
											</Row>
										</Flex>
									</Col>
								</Row>
							</Skeleton.Node>
						</Col>
					</Row>
				</Col>
			</Row>
		)
	} else if (
		[
			links.tickets.path,
			links.donate.path,
			links.pledge.path,
			links.surveys.path,
			links.polls.path,
		].some((path) => {
			/*
				Regex to detect -
				The path is exactly '/pledge'
				Or '/pledge/' (with trailing slash)
				But not '/pledge/123'
			*/
			const pattern = new RegExp(`(^|/)${path.replace(/^\//, '')}(/?$)`)
			return pattern.test(location.pathname)
		})
	) {
		let withInfo = true
		let ROWS = 2
		let CARD_CLASS = `!h-32`
		let CTA_BTN

		if ([links.surveys.path, links.polls.path].some((path) => location.pathname.includes(path))) {
			CARD_CLASS = `!h-52`
			withInfo = false
		}

		if ([links.donate.path, links.pledge.path].some((path) => location.pathname.includes(path))) {
			CARD_CLASS = `!h-48`
			CTA_BTN = (
				<Skeleton.Button active size={`large`} className='mb-5 mt-4 !h-14 !w-48 !rounded-full' />
			)
		}

		smHeader = true
		CONTENT = (
			<div className='flex flex-1 flex-col items-center pb-10'>
				<Flex vertical gap={24} className='w-full max-w-[856px]'>
					<Flex vertical align='center' className='mb-5'>
						<Skeleton.Button active size={`large`} className='!h-16 !w-52' />
						{withInfo && <Skeleton className='mt-5' active paragraph={{ rows: ROWS }} />}
						{CTA_BTN}
					</Flex>

					<Flex vertical gap={24}>
						{getListOf(8).map((i) => (
							<Skeleton.Button
								key={i}
								active
								size='large'
								block
								className={classNames('!rounded-xl', CARD_CLASS)}
							/>
						))}
					</Flex>
				</Flex>
			</div>
		)
	} else if (
		[links.auction_item.path, links.raffle_item.path, links.pledge_view.path].some((path) =>
			location.pathname.includes(path.replace(`:id`, '')),
		)
	) {
		smHeader = true
		contentWrapperMarginTop = 20
		mainContentWrapperClass = 'max-w-[1280px]'

		if (isFreshInitAppLoad && !showHeader) {
			/* Modify in a way to give a feel that no layout shift happening, everything loaded in one shot */
			marginTopCalcLogic = false
			showHeader = true
			uiWrapperClass = `!relative !z-[99999] !bg-white`
			uiWrapperStyle = { marginTop: `-${getHeaderHeightVar()}px` }
		}

		CONTENT = (
			<>
				<Row justify='center' className='w-full pb-10' gutter={[0, 20]}>
					<Col span={24} xl={15}>
						<Flex vertical gap={24} className='w-full lg:!pr-3.5 lg:pl-6'>
							<Skeleton.Node
								active
								className={classNames(`!h-[300px] !w-full !rounded-xl lg:!h-[419px]`)}
							>
								<Skeleton.Image />
							</Skeleton.Node>
						</Flex>
					</Col>
					<Col span={24} xl={9}>
						<Skeleton.Node active className={classNames('!rounded-xl', `!h-[378px] !w-full`)}>
							<Flex className='h-full w-full !p-4' vertical gap={16}>
								<Skeleton.Button
									active
									block
									size={`large`}
									className='!h-[92px] !w-full !rounded-xl'
								/>
								<Skeleton.Input active size={`large`} className='!h-3 !w-1/3' />
								<Flex gap={10}>
									{getListOf(3).map((i) => (
										<Skeleton.Button
											block
											key={i}
											active
											size={`large`}
											className='!h-12 !rounded-full'
										/>
									))}
								</Flex>
								<Skeleton.Input active size={`large`} className='!h-3 !w-1/3' />
								<Skeleton.Input active block size={`large`} className='!h-12' />
								<Skeleton.Button active block size={`large`} className='!h-12 !rounded-full' />
							</Flex>
						</Skeleton.Node>
					</Col>
				</Row>
				<Row justify='center' className='w-full pb-10' gutter={[20, 20]}>
					<Col span={24} xl={16}>
						<Flex vertical gap={24} className='w-full lg:px-6'>
							<Skeleton.Button active size={`large`} className='mb-5 !h-16 !w-full !max-w-96' />

							<Flex gap={24} wrap='wrap' className='w-full'>
								{getListOf(6).map((i) => (
									<Skeleton key={i} className='!w-full' active paragraph={{ rows: 3 }} />
								))}
							</Flex>
						</Flex>
					</Col>
					<Col span={24} xl={8}></Col>
				</Row>
			</>
		)
	} else if ([links.auction_ct.path].some((path) => location.pathname.includes(path))) {
		smHeader = true
		CONTENT = (
			<div className='flex flex-1 flex-col items-center pb-10'>
				<div className='container'>
					<Row justify='center'>
						<Col span={24} lg={20}>
							<Flex vertical gap={16} className='w-full'>
								<Flex vertical align='center' className=''>
									<Skeleton.Button active size={`large`} className='!h-12 !w-full !max-w-96' />
								</Flex>

								<AutoFitCardGrid
									cardProps={{ withButton: false, nodeClassName: '!h-[264px]', rows: 1 }}
								/>
							</Flex>
						</Col>
					</Row>
				</div>
			</div>
		)
	} else if (
		[
			links.live_auction.path,
			links.buy_now_items.path,
			links.sealed_auction.path,
			links.silent_auction.path,
			links.auction.path,
			links.fundraising.path,
			links.raffle.path,
			links.fundraising.path,
		].some((path) => location.pathname.includes(path))
	) {
		smHeader = true
		let infoSection = false
		let extraCards = false
		let headerCTA = false
		let subActionsSectionCount = 1

		if (isFreshInitAppLoad && !showHeader) {
			/* Modify in a way to give a feel that no layout shift happening, everything loaded in one shot */
			marginTopCalcLogic = false
			showHeader = true
			uiWrapperClass = `!relative !z-[99999] !bg-white`
			uiWrapperStyle = { marginTop: `-${getHeaderHeightVar()}px` }
		}

		if ([links.fundraising.path].some((path) => location.pathname.includes(path))) {
			extraCards = true
		}

		if ([links.raffle.path].some((path) => location.pathname.includes(path))) {
			infoSection = true
		}

		const CARDS_UI = (
			<AutoFitCardGrid
				cardProps={{
					nodeClassName: '!h-[430px]',
					imgClassName: '!h-[193px] !w-full !rounded-t-xl',
				}}
			/>
		)

		if (justCardsUI && horizontalCardUI) {
			return (
				<Flex vertical gap={24}>
					{getListOf(6).map((i) => (
						<Skeleton.Button
							key={i}
							active
							size='large'
							block
							className={classNames('!rounded-xl', '!h-40')}
						/>
					))}
				</Flex>
			)
		}
		if (justCardsUI) return CARDS_UI

		CONTENT = (
			<Row justify={`center`} gutter={[10, 10]} className=''>
				<Col span={24} lg={20}>
					<Flex vertical gap={24} className='w-full'>
						<Flex vertical align='center' className=''>
							<Skeleton.Button active size={`large`} className='!h-14 !w-full !max-w-96' />
							{infoSection && <Skeleton className='mt-10' active paragraph={{ rows: 2 }} />}
							{headerCTA && (
								<Skeleton.Button
									active
									size={`large`}
									className='mb-5 mt-4 !h-14 !w-40 !rounded-full'
								/>
							)}

							{extraCards && (
								<Flex justify='space-between' gap={20} className='w-full'>
									{getListOf(3).map((i) => (
										<Skeleton.Button
											key={i}
											active
											size={`large`}
											className='mb-14 mt-4 !h-40 !w-full !rounded-xl'
										/>
									))}
								</Flex>
							)}

							<Flex justify='center' vertical gap={20} className='w-full lg:hidden'>
								<Flex justify='space-between' className='mt-3' gap={20}>
									<Skeleton.Button active size={`large`} className='!h-12 !w-28 rounded-md' />
									<Skeleton.Button active size={`large`} className='!h-12 !w-28 rounded-md' />
								</Flex>
							</Flex>
							<Flex justify='space-between' gap={20} className='hidden w-full lg:flex'>
								{getListOf(subActionsSectionCount).map((i) => (
									<Skeleton.Button
										key={i}
										active
										size={`large`}
										className='!h-12 !w-52 rounded-md'
									/>
								))}
							</Flex>
						</Flex>

						{CARDS_UI}
					</Flex>
				</Col>
			</Row>
		)
	} else if ([links.leaderboard.path].some((path) => location.pathname.includes(path))) {
		showHeader = false
		withContainer = false

		CONTENT = (
			<Flex gap={20} justify='space-between' wrap='wrap' className='p-5'>
				{getListOf(6).map((i) => (
					<Skeleton.Button
						key={i}
						active
						size={`large`}
						className='!h-16 !w-full !max-w-96 !rounded-lg'
					/>
				))}

				<div className='mt-12 w-full'>
					{getListOf(6).map((i) => (
						<Skeleton key={i} className='!w-full' active paragraph={{ rows: 4 }} />
					))}
				</div>
			</Flex>
		)
	}

	if (
		showHeader ||
		(firstTimeCallingPageSchema &&
			/* Is home page */
			[links.root.path, links.home.path].includes(rrd_pathname))
	) {
		if (marginTopCalcLogic) contentWrapperMarginTop += headerSize?.height || 0
	}

	return (
		<>
			{HEAD}
			<div className={classNames('pb-10', uiWrapperClass)} style={uiWrapperStyle}>
				{showHeader && (
					<Skeleton.Node
						active
						fullSize
						className={classNames(`!w-full`)}
						style={{ height: smHeader ? getHeaderHeightVar() : 440 }}
					>
						{/* Top left logo */}
						<Skeleton.Image
							active
							className={classNames(`absolute left-3 !h-14`, {
								[showingBanner ? 'top-4' : 'top-2']: smHeader,
								[showingBanner ? 'top-6' : 'top-3']: !smHeader,
							})}
						/>

						{!smHeader && (
							<Skeleton.Button active size={`large`} shape={`round`} className='mt-10 !w-52' />
						)}

						<Flex
							gap={10}
							className={classNames(`absolute  right-8`, {
								[showingBanner ? 'top-10' : 'top-8']: smHeader,
								[showingBanner ? 'top-14' : 'top-10']: !smHeader,
							})}
						>
							<Skeleton.Button active className='' />
							<Skeleton.Button active className='' />
						</Flex>
					</Skeleton.Node>
				)}

				<div className={classNames(withContainer ? 'flex-center' : '', outerContentWrapperClass)}>
					<div
						style={{ marginTop: contentWrapperMarginTop }}
						className={classNames({ container: withContainer }, mainContentWrapperClass)}
					>
						{CONTENT}
					</div>
				</div>
			</div>
		</>
	)
}

export default AppSkeletonBase

const AutoFitCardGrid = ({ n = 6, cardProps = {} } = {}) => {
	return (
		<div className='w-full'>
			{/* Auto-fit grid that adjusts based on available space */}
			{/* <div
				className='grid gap-6'
				style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}
			> */}
			<div className='grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3'>
				{getListOf(n).map((i) => (
					<ItemCardVertical key={i} {...(cardProps || {})} />
				))}
			</div>
		</div>
	)
}

const ItemCardVertical = (props) => {
	const {
		withButton = true,
		nodeClassName = '!h-96',
		imgClassName = '!h-40 !w-full !rounded-t-xl',
		bodyClassName = '',
		rows = 2,
	} = props
	return (
		<Skeleton.Node active className={classNames('!w-full !rounded-xl', nodeClassName)}>
			<Flex className='h-full w-full' vertical gap={24}>
				<Skeleton.Image active className={classNames(imgClassName)} />
				<Flex className={classNames('h-full w-full px-5', bodyClassName)} vertical gap={24}>
					<Skeleton className='!w-full' active paragraph={{ rows }} />
					{withButton && (
						<Skeleton.Button active block size={`large`} className='!h-14 !rounded-full' />
					)}
				</Flex>
			</Flex>
		</Skeleton.Node>
	)
}

export const CheckoutLoadingShimmer = () => {
	return (
		<Flex
			vertical
			gap={32}
			className='mt-10 !w-full !min-w-[320px] max-w-[600px] md:mt-0'
			align='center'
		>
			<Skeleton.Button
				active
				size={`large`}
				className='!h-10 !w-full !max-w-sm !rounded-lg md:!h-16'
			/>

			{getListOf(2).map((i) => {
				return (
					<Skeleton.Node key={i} fullSize className='!h-[200px] !w-full !rounded-2xl'>
						<Skeleton active className='!w-full px-8 pt-3' paragraph={{ rows: 4 }} />
					</Skeleton.Node>
				)
			})}

			<Skeleton.Node className='!h-[400px] !w-full !rounded-2xl'>
				<Flex vertical gap={24} className='!w-full px-8'>
					{getListOf(5).map((i) => (
						<Skeleton.Button key={i} active size={`large`} className='!h-12 !w-full !rounded-lg' />
					))}
				</Flex>
			</Skeleton.Node>
		</Flex>
	)
}
